import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Wordmark } from "@/pages/Landing";
import { Seo } from "@/components/public/Seo";
import { FooterLegal } from "@/components/public/FooterLegal";
import publicContent from "@/pages/public/publicContent.json";

const DEMO_SEO = publicContent.routes.find((r) => r.path === "/richiedi-demo");

const GREEN = "#167A5A";
const ORANGE = "#F28C28";

const EMPTY = {
  nome: "", cognome: "", azienda: "", email: "", ruolo: "",
  telefono: "", numero_dipendenti: "", messaggio: "", privacy_accepted: false,
};

export default function RequestDemo() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSending(true);
    try {
      await api.post("/public/demo-requests", {
        ...form,
        numero_dipendenti: form.numero_dipendenti === "" ? null : Number(form.numero_dipendenti),
        telefono: form.telefono || null,
        messaggio: form.messaggio || null,
      });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#FAFBFA" }} data-testid="demo-page">
      <Seo title={DEMO_SEO.title} description={DEMO_SEO.description} path="/richiedi-demo" />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" aria-label="Torna alla home ESG-PMI"><Wordmark /></Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900"
            data-testid="demo-back-home">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-12">
        {done ? (
          <div className="rounded-2xl border bg-white p-10 text-center" style={{ borderColor: GREEN }}
            data-testid="demo-success">
            <CheckCircle2 className="w-12 h-12 mx-auto" style={{ color: GREEN }} aria-hidden="true" />
            <h1 className="mt-4 text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              Richiesta inviata
            </h1>
            <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
              Grazie {form.nome}. Abbiamo ricevuto la tua richiesta di demo per{" "}
              <strong>{form.azienda}</strong>: ti ricontatteremo al più presto all'indirizzo{" "}
              <strong>{form.email}</strong>.
            </p>
            <Link to="/"
              className="mt-6 inline-flex items-center gap-2 text-sm font-bold px-6 py-3 rounded-md text-white"
              style={{ background: GREEN }} data-testid="demo-success-home">
              Torna alla home
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
              Richiedi una demo
            </h1>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-xl">
              Raccontaci chi sei: ti mostreremo come ESG-PMI semplifica VSME, dossier banca
              e gestione ESG della tua impresa, integrato con JOBTIMEHR.
            </p>

            <form onSubmit={submit} className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 md:p-8 space-y-5"
              data-testid="demo-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="demo-nome" className="block text-sm font-semibold text-slate-800 mb-1.5">Nome *</label>
                  <input id="demo-nome" required value={form.nome} onChange={set("nome")} maxLength={100}
                    className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2"
                    style={{ "--tw-ring-color": GREEN }} data-testid="demo-nome" autoComplete="given-name" />
                </div>
                <div>
                  <label htmlFor="demo-cognome" className="block text-sm font-semibold text-slate-800 mb-1.5">Cognome *</label>
                  <input id="demo-cognome" required value={form.cognome} onChange={set("cognome")} maxLength={100}
                    className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2"
                    data-testid="demo-cognome" autoComplete="family-name" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="demo-azienda" className="block text-sm font-semibold text-slate-800 mb-1.5">Azienda *</label>
                  <input id="demo-azienda" required value={form.azienda} onChange={set("azienda")} maxLength={200}
                    className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2"
                    data-testid="demo-azienda" autoComplete="organization" />
                </div>
                <div>
                  <label htmlFor="demo-email" className="block text-sm font-semibold text-slate-800 mb-1.5">Email *</label>
                  <input id="demo-email" type="email" required value={form.email} onChange={set("email")} maxLength={200}
                    className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2"
                    data-testid="demo-email" autoComplete="email" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="demo-telefono" className="block text-sm font-semibold text-slate-800 mb-1.5">
                    Telefono <span className="font-normal text-slate-500">(facoltativo)</span>
                  </label>
                  <input id="demo-telefono" type="tel" value={form.telefono} onChange={set("telefono")} maxLength={30}
                    className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2"
                    data-testid="demo-telefono" autoComplete="tel" />
                </div>
                <div>
                  <label htmlFor="demo-ruolo" className="block text-sm font-semibold text-slate-800 mb-1.5">Sei *</label>
                  <select id="demo-ruolo" required value={form.ruolo} onChange={set("ruolo")}
                    className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2"
                    data-testid="demo-ruolo">
                    <option value="" disabled>Seleziona…</option>
                    <option value="IMPRESA">Un'impresa (micro/piccola/media)</option>
                    <option value="CONSULENTE">Un consulente / professionista</option>
                    <option value="ALTRO">Altro</option>
                  </select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="demo-dipendenti" className="block text-sm font-semibold text-slate-800 mb-1.5">
                    Numero dipendenti <span className="font-normal text-slate-500">(facoltativo)</span>
                  </label>
                  <input id="demo-dipendenti" type="number" min="0" max="1000000" value={form.numero_dipendenti}
                    onChange={set("numero_dipendenti")}
                    className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2"
                    data-testid="demo-dipendenti" />
                </div>
              </div>
              <div>
                <label htmlFor="demo-messaggio" className="block text-sm font-semibold text-slate-800 mb-1.5">Messaggio</label>
                <textarea id="demo-messaggio" rows={4} value={form.messaggio} onChange={set("messaggio")} maxLength={2000}
                  placeholder="Es. siamo una PMI manifatturiera e la banca ci ha chiesto informazioni ESG…"
                  className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 resize-none"
                  data-testid="demo-messaggio" />
              </div>
              <div className="flex items-start gap-2.5">
                <input id="demo-privacy" type="checkbox" required checked={form.privacy_accepted}
                  onChange={set("privacy_accepted")}
                  className="mt-1 w-4 h-4 rounded border-slate-300"
                  data-testid="demo-privacy" />
                <label htmlFor="demo-privacy" className="text-xs text-slate-600 leading-relaxed">
                  Ho letto l'informativa privacy e acconsento al trattamento dei dati per essere
                  ricontattato in merito alla richiesta di demo. *
                </label>
              </div>
              {error && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                  role="alert" data-testid="demo-error">{error}</p>
              )}
              <button type="submit" disabled={sending}
                className="inline-flex items-center gap-2 text-sm font-bold px-8 py-3.5 rounded-md text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                style={{ background: ORANGE }} data-testid="demo-submit">
                <Send className="w-4 h-4" aria-hidden="true" />
                {sending ? "Invio in corso…" : "Invia richiesta"}
              </button>
            </form>
          </>
        )}
      </main>
      <footer style={{ background: "#0C2540" }} data-testid="demo-footer">
        <div className="max-w-3xl mx-auto px-5 pb-8"><FooterLegal /></div>
      </footer>
    </div>
  );
}
